import { onMounted, onScopeDispose } from 'vue';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { useRouter } from 'vue-router';
import { useAuthStore } from '@/core/stores/auth.store';
import { PATIENT_AUTH_ENTRY_PATH } from '@/core/config/authentication';
import { APP_ONBOARDING_PATH, APP_ONBOARDING_STATUS } from '@/shared/constants/authentication';

// One root owner revalidates the session after real backgrounding, without polling.
export function useSessionLifecycle() {
  const auth = useAuthStore();
  const router = useRouter();
  const handles = [];
  let disposed = false;

  async function resume() {
    if (auth.isAuthReady || !auth.token) {
      return;
    }
    await auth.initAuth();
    if (disposed) {
      return;
    }
    if (!auth.isLoggedIn) {
      await router.replace(PATIENT_AUTH_ENTRY_PATH);
    } else if (auth.appOnboardingStatus === APP_ONBOARDING_STATUS.PENDING_TERMS_ACCEPTANCE) {
      await router.replace(APP_ONBOARDING_PATH.TERMS);
    } else if (auth.appOnboardingStatus === APP_ONBOARDING_STATUS.IN_PROGRESS) {
      await router.replace(APP_ONBOARDING_PATH.SETUP);
    }
  }

  onMounted(async () => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    for (const [event, callback] of [
      [
        'pause',
        () => {
          if (auth.isLoggedIn) {
            auth.isAuthReady = false;
          }
        },
      ],
      [
        'appStateChange',
        ({ isActive }) => {
          if (isActive) {
            void resume().catch(() => console.warn('[Authentication] Session validation failed'));
          }
        },
      ],
    ]) {
      const handle = await App.addListener(event, callback);
      if (disposed) {
        await handle.remove();
      } else {
        handles.push(handle);
      }
    }
  });

  onScopeDispose(() => {
    disposed = true;
    for (const handle of handles) {
      void handle.remove();
    }
  });
}
